/**
 * SeniorCalendarVoiceChat - A voice chat with all available tools
 * 
 * This implementation provides:
 * - Calendar tools (time, events)
 * - Knowledge graph tools (entities, relations)
 * - Any future tools added to the system
 */
import { VoiceChatBase, type VoiceChatOptions } from '../core/VoiceChatBase';
import { registerCalendarTools } from '../tools/calendarTools';
import { registerKnowledgeGraphTools } from '../tools/knowledgeGraphTools';
import { toolRegistry, type ToolOverridesMap, ToolName } from '../tools/ToolRegistry';
import { useCalendarStore } from '~/stores/useCalendarStore';
import { useKnowledgeGraphStore } from '~/stores/knowledgeGraphStore';
import { useLogger } from '~/composables/useLogger';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '~/stores/authStore';
import { registerChatControlTools } from '../tools/chatControlTools';

export class MelkFullVoiceChat extends VoiceChatBase {
  private i18n = useI18n();
  private authStore = useAuthStore();
  
  /**
   * Create a new full-featured voice chat instance with optional tool overrides
   * 
   * @param options Configuration options for the voice chat
   */
  constructor(options: VoiceChatOptions = {}) {
    // Call super first
    super(options);
    
    // Initialize tool overrides after super
    const defaultToolOverrides: ToolOverridesMap = {
      // Make calendar tools more contextual for this implementation
      // [ToolName.GET_CALENDAR_EVENTS]: {
      //   description: this.i18n.t('pages.senior_calendar_voice_chat.tools.get_calendar_events.description'),
      //   continuationInstructions: this.i18n.t('pages.senior_calendar_voice_chat.tools.get_calendar_events.continuation')
      // },
      // // Make getCurrentTime more informative
      // [ToolName.GET_CURRENT_TIME]: {
      //   description: this.i18n.t('pages.senior_calendar_voice_chat.tools.get_current_time.description'),
      //   continuationInstructions: this.i18n.t('pages.senior_calendar_voice_chat.tools.get_current_time.continuation')
      // }
    };
    
    // Merge default overrides with any provided in options
    const mergedOverrides = {
      ...defaultToolOverrides,
      ...(options.toolOverrides || {})
    };
    
    // Update tool overrides
    this.setToolOverrides(mergedOverrides);
  }
  
  /**
   * Get initial prompt for the full-featured assistant
   */
  public getInitialPrompt(): string {
    const seniorName = this.authStore.userPerson?.firstname || '';
    return `You are Nestor, an assistant that helps a user that is a caregiver for a senior called ${seniorName}. You can help the user add, edit, and delete events from the calendar for ${seniorName}. At your disposal are tools for understanding what's in the calendar and to add and delete events in the calendar.
    `
    // return this.i18n.t('pages.senior_calendar_voice_chat.initial_prompt', { name: seniorName });
  }

  /**
   * Register all available tools
   */
  protected async registerTools(): Promise<void> {
    const logger = useLogger();
    logger.info('FullFeaturedVoiceChat: Registering all tools');
    
    // Get stores
    const calendarStore = useCalendarStore();
    // const knowledgeGraphStore = useKnowledgeGraphStore();
    
    // Initialize the knowledge graph if needed
    // if (!knowledgeGraphStore.ready) {
    //   logger.info('FullFeaturedVoiceChat: Initializing knowledge graph store');
    //   await knowledgeGraphStore.initialize();
    // }
    
    // Register calendar tools
    logger.info('FullFeaturedVoiceChat: Registering calendar tools');
    registerCalendarTools(calendarStore, true, false);

    // Register chat control tools with callback to this instance's initiateShutdown method
    logger.info('FullFeaturedVoiceChat: Registering chat control tools');
    registerChatControlTools(this.initiateShutdown.bind(this));

    // Register knowledge graph tools
    // logger.info('FullFeaturedVoiceChat: Registering knowledge graph tools');
    // registerKnowledgeGraphTools(knowledgeGraphStore);
    
    // Mark registry as initialized
    toolRegistry.setInitialized();
    
    // Log registered tools
    const registeredTools = toolRegistry.getRegisteredToolNames();
    logger.info(`FullFeaturedVoiceChat: Registered ${registeredTools.length} tools: ${registeredTools.join(', ')}`);
  }  
} 